import * as THREE from 'three'

/**
 * Roblox の Fire エフェクトを模した炎パーティクル。
 *
 * 加算合成のビルボード（Sprite）を多数立ち上らせ、寿命に沿って
 * 黄 → 橙 → 赤 と色を変えながら縮小・減衰させることで、メラメラと
 * 揺らめく炎を近似する。ジオメトリやテクスチャ画像は持ち込まず、
 * canvas の放射グラデーション 1 枚を全パーティクルで共有する。
 *
 * 親（MeshEntry.container）はオブジェクトの size でスケールされる契約のため、
 * 本エフェクトは単位空間（直径 1 の球に内接する想定）で生成する。
 */
export interface FireEffectHandle {
  /** container へ追加するパーティクル群のルート。 */
  readonly group: THREE.Group
  /** 1 フレーム分パーティクルを進める。dt は秒。 */
  update(dt: number): void
  /** テクスチャ・マテリアルを解放する。group の除去は呼び出し側が行う。 */
  dispose(): void
}

/** パーティクル数。少なすぎると炎が途切れ、多すぎると重い。 */
const PARTICLE_COUNT = 36

/** パーティクル寿命の下限・上限（秒）。ばらつきが揺らめきになる。 */
const LIFE_MIN_SECONDS = 0.4
const LIFE_MAX_SECONDS = 0.9

/** 炎の色変化（誕生 → 中間 → 消滅）。Roblox Fire の黄核 → 橙 → 赤縁に合わせる。 */
const COLOR_BIRTH = new THREE.Color('#ffe08a')
const COLOR_MID = new THREE.Color('#ff7a1a')
const COLOR_DEATH = new THREE.Color('#d62500')

/** 単位空間での発生半径（0.5 = 親オブジェクトの表面近く）。 */
const SPAWN_RADIUS = 0.5
/** 単位空間での上昇量（寿命を通して y がこれだけ上がる）。 */
const RISE_DISTANCE = 1.35
/** 単位空間でのスプライト初期スケール。 */
const SPRITE_SCALE_BIRTH = 1.0
/** 消滅時のスプライトスケール（縮みながら消える）。 */
const SPRITE_SCALE_DEATH = 0.2

/** 炎 1 枚ぶんの放射グラデーションテクスチャを生成する（白核 → 透明）。 */
const createFlameTexture = (): THREE.CanvasTexture => {
  const size = 64
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const context = canvas.getContext('2d')
  if (context) {
    const gradient = context.createRadialGradient(
      size / 2,
      size / 2,
      0,
      size / 2,
      size / 2,
      size / 2,
    )
    gradient.addColorStop(0, 'rgba(255, 255, 255, 1)')
    gradient.addColorStop(0.35, 'rgba(255, 255, 255, 0.55)')
    gradient.addColorStop(1, 'rgba(255, 255, 255, 0)')
    context.fillStyle = gradient
    context.fillRect(0, 0, size, size)
  }
  const texture = new THREE.CanvasTexture(canvas)
  texture.colorSpace = THREE.SRGBColorSpace
  return texture
}

/** 1 パーティクルの状態。respawn のたびに初期化される。 */
interface FlameParticle {
  readonly sprite: THREE.Sprite
  readonly material: THREE.SpriteMaterial
  /** 経過秒。life を超えたら respawn する。 */
  age: number
  /** このサイクルの寿命（秒）。 */
  life: number
  /** 発生位置（単位空間）。 */
  baseX: number
  baseY: number
  baseZ: number
  /** 横方向の揺らぎ位相（メラメラ感）。 */
  swayPhase: number
}

/** 発生パラメータを乱数で振り直す。 */
const respawn = (particle: FlameParticle): void => {
  // 球内の一様分布に近い発生点（上方に伸びるので下半球寄りに置く）。
  const theta = Math.random() * Math.PI * 2
  const radius = SPAWN_RADIUS * Math.sqrt(Math.random())
  particle.baseX = Math.cos(theta) * radius
  particle.baseZ = Math.sin(theta) * radius
  particle.baseY = (Math.random() - 0.6) * SPAWN_RADIUS
  particle.life =
    LIFE_MIN_SECONDS + Math.random() * (LIFE_MAX_SECONDS - LIFE_MIN_SECONDS)
  particle.age = Math.random() * particle.life * 0.25
  particle.swayPhase = Math.random() * Math.PI * 2
}

/** 寿命進行度 t（0..1）に応じてスプライトの位置・色・透明度・スケールを反映する。 */
const applyParticle = (particle: FlameParticle, t: number): void => {
  const sway = Math.sin(particle.swayPhase + t * 7) * 0.16 * (1 - t)
  particle.sprite.position.set(
    particle.baseX + sway,
    particle.baseY + RISE_DISTANCE * t,
    particle.baseZ,
  )
  const scale =
    SPRITE_SCALE_BIRTH + (SPRITE_SCALE_DEATH - SPRITE_SCALE_BIRTH) * t
  particle.sprite.scale.set(scale, scale, scale)
  // 色は 黄(0) → 橙(0.5) → 赤(1) の 2 段階補間。
  if (t < 0.5) {
    particle.material.color.lerpColors(COLOR_BIRTH, COLOR_MID, t * 2)
  } else {
    particle.material.color.lerpColors(COLOR_MID, COLOR_DEATH, (t - 0.5) * 2)
  }
  // 立ち上がりで一気に明るくなり、後半で減衰する。
  particle.material.opacity = Math.min(1, t * 5) * (1 - t) * 0.95
}

/**
 * 炎エフェクトを生成する。返した group を対象オブジェクトの container に
 * 追加し、レンダラの描画ループから update(dt) を呼ぶこと。
 */
export const createFireEffect = (): FireEffectHandle => {
  const texture = createFlameTexture()
  const group = new THREE.Group()
  // 本体メッシュより手前に加算合成で重ねる。深度書き込みを切り、
  // 炎同士・本体との前後関係でちらつかないようにする。
  group.renderOrder = 1

  const particles: FlameParticle[] = []
  for (let i = 0; i < PARTICLE_COUNT; i += 1) {
    const material = new THREE.SpriteMaterial({
      map: texture,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      transparent: true,
    })
    const sprite = new THREE.Sprite(material)
    // 炎はクリック判定・遮蔽フェードのレイキャスト対象にしない。
    // （Sprite.raycast は raycaster.camera 前提のため、カメラ未設定の
    //   intersectObjects 再帰に巻き込まれると例外になる）
    sprite.raycast = () => undefined
    const particle: FlameParticle = {
      sprite,
      material,
      age: 0,
      life: LIFE_MIN_SECONDS,
      baseX: 0,
      baseY: 0,
      baseZ: 0,
      swayPhase: 0,
    }
    respawn(particle)
    // 初期フレームから炎が「燃えている途中」に見えるよう寿命をばらして開始する。
    particle.age = Math.random() * particle.life
    applyParticle(particle, particle.age / particle.life)
    particles.push(particle)
    group.add(sprite)
  }

  const update = (dt: number): void => {
    for (const particle of particles) {
      particle.age += dt
      if (particle.age >= particle.life) {
        respawn(particle)
      }
      applyParticle(particle, particle.age / particle.life)
    }
  }

  const dispose = (): void => {
    for (const particle of particles) {
      particle.material.dispose()
    }
    texture.dispose()
  }

  return { group, update, dispose }
}
